import { Floor } from "shared/types/Floor"
import { ProductPosition } from "shared/types/ProductPosition"
import { Room } from "shared/types/Room"
import {
  Project as GeneratedProject,
  ProductPositionAddon
} from "shared/types/__generated__/graphql"

import { ArrayElement } from "./ArrayElement"

export type ProjectChild = Floor | Room | ProductPosition

export interface Project extends Omit<GeneratedProject, "children"> {
  children?: ProjectChild[] | null
}

export const ProjectsAPIType = "projects" as const
export const ProjectPhasesAPIType = "project_phases" as const
export const FloorsAPIType = "floors" as const
export const RoomsAPIType = "rooms" as const
export const ProductPositionsAPIType = "project_positions" as const
export const ProductInformationAPIType =
  "aggregated_product_informations" as const
export const ArticleNumberSearchResultsAPIType =
  "articel_number_search_results" as const
export const SpecialPriceGroupsAPIType = "special_price_groups" as const
export const FavoritesAPIType = "favorites" as const
export const ProcessesAPIType = "processes" as const
export const DuplicateEntitiesAPIType = "duplicate_entities" as const
export const InflationSurchargesAPIType = "inflation_surcharges" as const
export const UserPermissionsAPIType = "user_permissions" as const
export const ProjectHistoryEntriesAPIType = "changelog_entries" as const

export type ProjectParentAPIType =
  | typeof ProjectsAPIType
  | typeof FloorsAPIType
  | typeof RoomsAPIType
  | typeof FavoritesAPIType

export const ProjectTypename = "Project" as const
export const ProjectPhaseTypename = "ProjectPhase" as const
export const ProjectUITypename = "ProjectUI" as const
export const FloorTypename = "Floor" as const
export const RoomTypename = "Room" as const
export const ProductPositionTypename = "ProductPosition" as const

const PROJECT_PARENT_TYPENAMES = [ProjectTypename, FloorTypename, RoomTypename]

export type ProjectParentTypename = ArrayElement<
  typeof PROJECT_PARENT_TYPENAMES
>
export function isProjectParentTypename(
  value: unknown
): value is ProjectParentTypename {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return PROJECT_PARENT_TYPENAMES.includes(value as any)
}

export const ProductPositionAddonTypename = "ProductPositionAddon" as const
export const FavoritesTypename = "Favorites" as const
export const SpecialPriceGroupTypename = "SpecialPriceGroup" as const
export const ShareLinkTypename = "ShareLink" as const

export type ProjectItemType =
  | typeof ProjectTypename
  | typeof FloorTypename
  | typeof RoomTypename
  | typeof ProductPositionTypename
  | typeof SpecialPriceGroupTypename

export const NEW_PROJECT_COLOR = "#005B32"
export const AVAILABLE_PROJECT_COLORS = [
  "#F14800",
  "#F18700",
  "#F2C94C",
  "#005B32",
  "#009959",
  "#66C29B",
  "#006D75",
  "#009CA7",
  "#56CCF2",
  "#770D48",
  "#AA1267",
  "#BB4185"
]

export const NO_PROJECT_COLOR = "#606060"

export const MAX_LENGTH_PROJECT_NAME = 90

export interface ProductPositionResponseData {
  id: string
  type: typeof ProductPositionsAPIType
  attributes: {
    variantKey: string
    quantity: number
    projectId: string
    position?: number | null
    remark: string | null
  }
}

export interface FloorResponseData {
  id: string
  type: typeof FloorsAPIType
  attributes: {
    label?: string | null
    position?: number | null
    children?: RoomResponseData[]
  }
}

export interface GetFloorResponse {
  data: FloorResponseData
}

export interface RoomResponseData {
  id: string
  type: typeof RoomsAPIType
  attributes: {
    label?: string | null
    position?: number | null
    children?: ProjectChildResponse[]
  }
}

export interface GetRoomResponse {
  data: RoomResponseData
}

export type ProjectChildResponse =
  | FloorResponseData
  | RoomResponseData
  | ProductPositionResponseData

export interface ProjectResponseData {
  id: string
  type: typeof ProjectsAPIType
  attributes: Omit<Project, "id" | "productPositions">
}

export interface GetProjectResponse {
  data: ProjectResponseData
}

export function isProjectFloor(
  value: unknown | null | undefined
): value is Floor {
  return typeof value === "object" && !!value && "__typename" in value
    ? value.__typename === FloorTypename
    : false
}

export function isProjectRoom(
  value: unknown | null | undefined
): value is Room {
  return typeof value === "object" && !!value && "__typename" in value
    ? value.__typename === RoomTypename
    : false
}

export function isProjectProductPosition(
  value: unknown | null | undefined
): value is ProductPosition {
  return typeof value === "object" && !!value && "__typename" in value
    ? value.__typename === ProductPositionTypename
    : false
}

export function isProjectProductPositionAddon(
  value: unknown | null | undefined
): value is ProductPositionAddon {
  return typeof value === "object" && !!value && "__typename" in value
    ? value.__typename === ProductPositionAddonTypename
    : false
}

export interface MinimalProject {
  id: string
  title: string
  __typename?: typeof ProjectTypename
}
